/**
 * Sell ORB tokens via OTC — main account manual listing only (not sub-accounts).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { otcAPI } from '../api/otc';
import { usersAPI } from '../api/users';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
} from '../components/ui/ThemedSurface';

interface BalancesResponse {
  balances: Record<string, string>;
}

const UO_PRICE_USD = 0.233;
const MIN_SELL_AMOUNT = 100;

function SellUOStatsPanel({ balance }: { balance: string }) {
  const { t } = useTranslation();
  const balanceValue = Number(balance);
  const totalValue = (balanceValue * UO_PRICE_USD).toFixed(2);

  return (
    <ThemedPanel className="overflow-hidden p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }} />
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-medium" style={{ color: C.muted }}>{t('sellUO.uoQty')}</div>
            <div className="mt-1 flex items-end gap-1">
              <span className="truncate font-mono text-[28px] font-semibold leading-none" style={{ color: C.text, textShadow: `0 0 14px ${C.gold}22` }}>
                {balanceValue.toFixed(2)}
              </span>
              <span className="pb-0.5 text-xs font-semibold" style={{ color: C.gold }}>ORB</span>
            </div>
          </div>
          <div className="rounded-full border px-3 py-1 text-[10px] font-semibold" style={{ borderColor: C.softBorder, background: C.chipBg, color: C.gold }}>
            LIVE
          </div>
        </div>

        <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border px-3 py-3" style={{ borderColor: C.softBorder, background: C.inputBg }}>
          <div className="min-w-0">
            <div className="text-[10px] leading-none" style={{ color: C.muted }}>{t('sellUO.livePrice')}</div>
            <div className="mt-1 truncate font-mono text-base font-semibold leading-none" style={{ color: C.gold }}>
              ${UO_PRICE_USD.toFixed(3)}
            </div>
          </div>
          <div className="h-9 w-px" style={{ background: C.statDividerAlt }} />
          <div className="min-w-0 text-right">
            <div className="text-[10px] leading-none" style={{ color: C.muted }}>{t('sellUO.approxValue')}</div>
            <div className="mt-1 truncate font-mono text-base font-semibold leading-none" style={{ color: C.buy }}>
              ${totalValue}
            </div>
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

export function SellUOPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [amount, setAmount] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: balData } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances() as Promise<BalancesResponse>,
    refetchInterval: 15_000,
  });

  const { data: bookData } = useQuery({
    queryKey: ['otc', 'book', 'sell-uo'],
    queryFn: () => otcAPI.getOrderBook({ depth: 5 }),
    staleTime: 30_000,
  });

  const suggestedApPrice = bookData?.sell?.[0]?.price ?? bookData?.buy?.[0]?.price ?? '';

  const uoBalance = balData?.balances?.uo ?? '0';
  const totalAp = amount && unitPrice && Number(amount) > 0 && Number(unitPrice) > 0
    ? (Number(amount) * Number(unitPrice)).toFixed(4)
    : '—';
  const formDisabled = !amount.trim() || !unitPrice.trim();

  const sellMut = useMutation({
    mutationFn: () =>
      otcAPI.placeOrder({
        side: 'sell',
        amount_uo: amount.trim(),
        unit_price: unitPrice.trim(),
      }),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setTimeout(() => navigate('/trading/hall?tab=orders'), 1500);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('sellUO.sellFailed'));
    },
  });

  function handleSubmit() {
    setError(null);
    const numAmount = Number(amount);
    if (!amount || numAmount <= 0) {
      setError(t('sellUO.amountError'));
      return;
    }
    if (numAmount < MIN_SELL_AMOUNT) {
      setError(t('sellUO.minAmountError', { amount: MIN_SELL_AMOUNT }));
      return;
    }
    if (numAmount > Number(uoBalance)) {
      setError(t('sellUO.exceedBalanceError'));
      return;
    }
    if (!unitPrice || Number(unitPrice) <= 0) {
      setError(t('otc.enterUnitPrice', '请输入单价'));
      return;
    }
    sellMut.mutate();
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('sellUO.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: '12px' }}>
          <SellUOStatsPanel balance={uoBalance} />

          <ThemedPanel className="p-4">
            <div className="flex flex-col gap-5">
              <div className="rounded-xl border px-3 py-2 text-xs leading-relaxed" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>
                {t('sellUO.manualHint', '手动挂卖主账户 ORB。注册新会员时会自动为下线排队 500 ORB 出售，与子账户无关。')}
              </div>
              <div className="flex flex-col gap-3">
                <ThemedField label={t('sellUO.amount')} required>
                  <div className="relative">
                    <ThemedInput type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t('sellUO.amountPlaceholder')} min="0" step="any" className="pr-16" />
                    <button
                      type="button"
                      onClick={() => setAmount(uoBalance)}
                      className="absolute right-2 top-1/2 flex h-7 -translate-y-1/2 items-center px-2 text-[11px] font-semibold transition-all active:scale-[0.98]"
                      style={{ color: C.gold }}
                    >
                      {t('common.all_btn')}
                    </button>
                  </div>
                </ThemedField>
                <ThemedField label={`${t('common.unitPrice', '单价')} (AP / ORB)`} required>
                  <div className="relative">
                    <ThemedInput
                      type="number"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                      placeholder={suggestedApPrice ? String(suggestedApPrice) : t('otc.enterUnitPrice', '请输入单价')}
                      min="0"
                      step="any"
                      className="pr-16"
                    />
                    {suggestedApPrice ? (
                      <button
                        type="button"
                        onClick={() => setUnitPrice(String(suggestedApPrice))}
                        className="absolute right-2 top-1/2 flex h-7 -translate-y-1/2 items-center px-2 text-[11px] font-semibold transition-all active:scale-[0.98]"
                        style={{ color: C.gold }}
                      >
                        {t('common.market', '市价')}
                      </button>
                    ) : null}
                  </div>
                </ThemedField>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    <div className="text-xs leading-none">{t('sellUO.minSell')}</div>
                    <div className="text-xs leading-none font-mono font-semibold" style={{ color: C.text }}>{`${MIN_SELL_AMOUNT} ORB`}</div>
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <div className="text-xs leading-none">{t('common.totalAmount', '总额')} (AP)</div>
                    <div className="text-xs leading-none font-mono font-semibold" style={{ color: C.buy }}>{totalAp}</div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border px-3 py-2.5 text-xs leading-relaxed" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>
                {t('sellUO.feeNotice')}
              </div>
            </div>
          </ThemedPanel>

          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
          {success && <ThemedNotice tone="success">{t('sellUO.successMsg')}</ThemedNotice>}
        </div>

        <ThemedBottomActionBar background={C.bottomBarBg}>
          <ThemedSubmitButton disabled={success || sellMut.isPending || formDisabled} onClick={handleSubmit}>
            {sellMut.isPending ? '…' : t('sellUO.submit')}
          </ThemedSubmitButton>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}
